import React, { useState, useEffect, useRef } from "react";
import { Form, Row, Col, Input, Button, Spin, message } from "antd";
import { useHistory } from "react-router-dom";
import { Link } from "react-router-dom";
import { clock } from "../../utils/clock";
import { MinusOutlined, PlusOutlined, LoadingOutlined } from "@ant-design/icons";
import { Scrollbars } from "react-custom-scrollbars";
import MenuIcon from "../../assets/images/icon.png";
import db from "../../database";
import data from "../../constants/opentillClosetill.json";
import "../style.css";
import "../Till/TillScreens.css";
import dayjs from "dayjs";
import { getMultilingualText } from "../../utils/getMultilingualText";
import messages from "../../errors/messages.en-IN.json";
import useAuthStore from "../../stores/useAuthStore";
import usePOSConfigStore from "../../stores/usePOSConfigStore";
import useSessionStore from "../../stores/useSessionStore";
import { trace } from "../../utils/debugTrace";

const CloseTill = () => {
  const tillData = useAuthStore((s) => s.tillData);
  const currenciesList = tillData?.tillAccess?.csBunit?.currencies || [];
  const payments = tillData?.tillAccess?.csBunit?.paymentMethodList || [];
  const tillId = tillData?.tillAccess?.cwrTill?.cwrTillID;
  const paymentMethodList = payments.filter((payment) => !payment.cwrTillId || payment?.cwrTillId === tillId);
  const tillSession = useSessionStore.getState().tillSession;
  const [loading, setLoading] = useState(false);
  const sessionId = tillSession?.tillSessionId;
  const history = useHistory();

  const [currencyType, setCurrencyType] = useState({
    currSymbolLeft: "₹",
    currSymbolRight: "",
    stdPrecision: 2,
  });
  const [cashExpectedAmount, setCashExpectedAmount] = useState(0);
  const [denominations, setDenominations] = useState([]);
  const [allPaymentsData, setAllPaymentsData] = useState([]);
  const [totalOrdersCount, setTotalOrdersCount] = useState(0);
  const [totalOpeningAmount, setTotalOpeningAmount] = useState(0);
  const [cashSaleAmount, setCashSaleAmount] = useState(0);
  const [cashReturnAmount, setCashReturnAmount] = useState(0);
  const [salesAmount, setSalesAmount] = useState(0);
  const [advanceAmount, setAdvanceAmount] = useState(0);
  const [cashValues, setCashValues] = useState({
    cashIn: 0,
    cashOut: 0,
    pettyCashIn: 0,
    pettyCashOut: 0,
  });
  const [hideBackButton, setHideBackButton] = useState(false);

  const posConfig = usePOSConfigStore((s) => s.posConfig);

  // Guard: if till data or session is missing, redirect to till selection.
  useEffect(() => {
    trace("CloseTill", "guard check", {
      hasTillAccess: !!tillData?.tillAccess,
      tillAccessIsArray: Array.isArray(tillData?.tillAccess),
      hasCsBunit: !!tillData?.tillAccess?.csBunit,
      hasTillSession: !!tillSession,
      sessionId: tillSession?.tillSessionId?.slice(0, 8),
    });
    if (!tillData?.tillAccess?.csBunit || !tillSession) {
      trace("CloseTill", "guard FIRED → /till");
      history.replace("/till");
    }
  }, []);

  useEffect(() => {
    const checkTillEvents = async () => {
      const tillEvents = await db?.tillEvents.where("tillStatus").equals("open").toArray();
      if (tillEvents.length > 0) {
        const currentDate = dayjs().format("YYYY-MM-DD");
        const tillEventDate = tillEvents[0]?.openingTime?.split(" ")[0];
        if (currentDate !== tillEventDate) {
          setHideBackButton(true); // hide button
        }
      }
    };

    checkTillEvents();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!tillSession) return history.push("/till");

      let cashStats = { cashIn: 0, cashOut: 0, pettyCashIn: 0, pettyCashOut: 0 };

      try {
        setLoading(true);
        // Fetch cash transactions
        const cashDetails = await db.cashInCashOut.toArray();
        cashDetails.forEach(({ type, amount }) => {
          cashStats[type] += parseFloat(amount || 0);
        });
        setCashValues(cashStats);

        // Fetch currency details
        const registrations = await db.tillRegistrations.toArray();
        const currency = registrations[0]?.tillAccess?.[0]?.csBunit?.csCurrency;
        if (currency) {
          setCurrencyType({
            currSymbolLeft: currency.symbolrightside ? "" : currency.currSymbol,
            currSymbolRight: currency.symbolrightside ? currency.currSymbol : "",
            stdPrecision: currency.stdPrecision || 2,
          });
        }

        // Fetch orders for the session
        const orders = await db.orders.where("tillSessionId").equals(sessionId).toArray();
        const tillEvent = await db.tillEvents.where("tillSessionId").equals(sessionId).toArray();
        setTotalOrdersCount(orders.length);

        // BUG-2236: db.paymentsData.orderPayments (DI-09) is the append-only log of
        // every payment taken while THIS till session was open, keyed by sOrderID.
        // For a retrieved advance/layaway order it is the only trustworthy record of
        // what this session actually collected -- the order row's `paymentData` holds
        // deposits folded in from ANY earlier session by getSalesHistoryData, and its
        // `payments[]` is overwritten with just the balance on completion. The old
        // gate keyed on `deliveryDate`, which processOrderApproved resets to "now"
        // when the order finally completes, so a prior day's deposit re-counted into
        // whichever session closed the order.
        const sessionPaymentsRow = (await db.paymentsData.where("tillSessionId").equals(sessionId).toArray())[0];
        const orderPaymentsBySOrderId = new Map();
        (sessionPaymentsRow?.orderPayments || []).forEach((op) => {
          if (!op?.sOrderID) return;
          const list = orderPaymentsBySOrderId.get(op.sOrderID) || [];
          list.push(op);
          orderPaymentsBySOrderId.set(op.sOrderID, list);
        });
        const hasSessionPaymentLog = !!sessionPaymentsRow;

        // Process payments
        const updatedPayments = paymentMethodList.map((payment) => {
          let expectedAmount = 0,
            paymentReturn = 0,
            transactionCount = 0;

          orders.forEach(({ sOrderID, total, payments = [], paymentData = [], deliveryDate, isReturn }) => {
            // BUG-1876: classify by the order's own isReturn flag, not total's sign.
            // total can end up <= 0 for reasons other than a genuine return (e.g. a
            // stale-identity overwrite corrupting an already-paid order's total), which
            // previously siphoned that order's real card/cash sale into the Return
            // column even though no actual sales return exists for it.
            const orderIsReturn = isReturn === true || isReturn === "Y";
            const isRetrievedAdvance = Array.isArray(paymentData) && paymentData.length > 0;
            const sessionOrderPayments = orderPaymentsBySOrderId.get(sOrderID);

            // BUG-2236: for a retrieved advance/layaway order, count only what the
            // per-session payment log recorded against it -- this skips its own
            // (unreliable) payments[]/paymentData entirely.
            if (isRetrievedAdvance && hasSessionPaymentLog && sessionOrderPayments?.length) {
              sessionOrderPayments.forEach((op) => {
                (op.payments || []).forEach(({ name, amount }) => {
                  if (name && payment.name.toLowerCase() === name.toLowerCase()) {
                    const value = parseFloat(amount || 0);
                    orderIsReturn ? (paymentReturn += Math.abs(value)) : (expectedAmount += value);
                    transactionCount++;
                  }
                });
              });
              return;
            }

            payments.forEach(({ name, amount }) => {
              if (payment.name.toLowerCase() === name.toLowerCase()) {
                const value = parseFloat(amount || 0);
                orderIsReturn ? (paymentReturn += Math.abs(value)) : (expectedAmount += value);
                transactionCount++;
              }
            });
            // Fallback for a retrieved advance the session log has no entry for (order
            // predates DI-09 / an exotic save path): keep the prior deliveryDate window.
            if (isRetrievedAdvance) {
              const now = dayjs();
              const openingMoment = dayjs(tillSession.openingTime, "YYYY-MM-DD HH:mm:ss");
              const orderMoment = dayjs(deliveryDate, "DD-MM-YYYY HH:mm:ss");
              if ((orderMoment.isAfter(openingMoment) || orderMoment.isSame(openingMoment)) && (orderMoment.isBefore(now) || orderMoment.isSame(now))) {
                paymentData.forEach(({ name, amount }) => {
                  if (payment.name.toLowerCase() === name.toLowerCase()) {
                    const value = parseFloat(amount || 0);
                    orderIsReturn ? (paymentReturn += Math.abs(value)) : (expectedAmount += value);
                    transactionCount++;
                  }
                });
              }
            }
          });

          tillEvent.forEach(({ walletTransactions = [] }) => {
            walletTransactions?.forEach((transaction) => {
              if (transaction?.finPaymentmethodID === payment?.finPaymentmethodId) {
                const value = parseFloat(transaction?.amount || 0);
                expectedAmount += value;
                transactionCount++;
              }
            });
          });

          return {
            ...payment,
            expectedAmount,
            amount: 0,
            paymentReturn,
            difference: Math.abs(paymentReturn) - expectedAmount,
            transactionCount,
          };
        });
        setAllPaymentsData(updatedPayments);

        // Handle cash payments
        const cashPayment = updatedPayments.find((p) => p.name.toLowerCase() === "cash");
        if (cashPayment) {
          const { expectedAmount, paymentReturn, transactionCount } = cashPayment;
          const openingAmount = useSessionStore.getState().tillSession?.totalOpeningAmount || 0;
          const includeOpening = tillData?.tillAccess?.csBunit?.incDayOpening !== "N";

          setTotalOpeningAmount(openingAmount);
          setCashSaleAmount(expectedAmount);
          setCashReturnAmount(paymentReturn);
          setCashExpectedAmount(
            expectedAmount - paymentReturn + cashStats.cashIn + cashStats.pettyCashIn - cashStats.pettyCashOut - cashStats.cashOut + (includeOpening ? openingAmount : 0)
          );
        }

        // Update till session details
        // const tillEvent = await db.tillEvents.where("tillSessionId").equals(sessionId).toArray();
        if (tillEvent.length > 0 && tillEvent[0]?.cashInOutData) {
          setCashValues(tillEvent[0].cashInOutData);
          cashStats = tillEvent[0].cashInOutData;
        }

        const updatedTillSession = { ...tillSession };
        const cashIndex = updatedPayments.findIndex((p) => p.name.toLowerCase() === "cash");
        const closingCash = tillSession?.closingCash || 0;
        let updatedAmount = 0;
        if (cashIndex !== -1) {
          updatedAmount =
            parseFloat(updatedPayments[cashIndex].expectedAmount) + cashStats.cashIn - cashStats.cashOut + (cashStats.pettyCashIn || 0) - (cashStats.pettyCashOut || 0);
          if (tillData?.tillAccess?.csBunit?.incDayOpening !== "N") {
            updatedAmount += parseFloat(totalOpeningAmount || 0);
          }

          updatedPayments[cashIndex] = {
            ...updatedPayments[cashIndex],
            expectedAmount: updatedAmount,
            amount: closingCash,
            actualAmount: closingCash,
            cashSaleAmount: parseFloat(cashSaleAmount?.toFixed(tillData?.tillAccess?.csBunit?.currencies?.[0]?.prcPrecision || 2)),
            difference: parseFloat(
              (closingCash - (updatedAmount - updatedPayments[cashIndex].paymentReturn)).toFixed(tillData?.tillAccess?.csBunit?.currencies?.[0]?.prcPrecision || 2)
            ),
          };
        }

        // BUG-2019: closingTime must NOT be stamped here — this effect runs on every mount
        // of this screen, including a cashier just browsing in and abandoning without
        // confirming. Stamping it early left the till record showing tillStatus "open"
        // with a closing time already filled in if the flow was abandoned anywhere in the
        // 3-screen close-till flow. closingTime is now set once, at actual confirm time,
        // in cashToKeep.js's excuteCloseTillAll.
        Object.assign(updatedTillSession, {
          closingCash,
          closingCashDenominations: denominations || [],
          allPaymentsData: updatedPayments,
          totalOrdersCount,
        });

        await db.tillEvents.where("tillSessionId").equals(updatedTillSession.tillSessionId).modify(updatedTillSession);
        useSessionStore.getState().setTillSession(updatedTillSession);
        if ((cashIndex === -1 || posConfig?.showDenominations === "N") && posConfig?.showCloseTillPaymentSummary === "Y") {
          history.push("/close-till-all");
        }
        setLoading(false);
      } catch (error) {
        setLoading(false);
        console.error("Error during data fetch:", error);
      }
    };

    fetchData();
  }, []);

  // Manage denominations
  useEffect(() => {
    if (tillSession?.closingCashDenominations?.length > 0) {
      setDenominations(tillSession.closingCashDenominations);
    } else {
      const sortedDenominations = currenciesList[0]?.denominations
        ?.map((denom) => ({
          ...denom,
          count: 0,
          amount: 0,
        }))
        .sort((a, b) => a.value - b.value);
      setDenominations(sortedDenominations || []);
    }
  }, []);

  // Set total amount
  useEffect(() => {
    if (tillSession?.closingCash) {
      setTotalAmount(tillSession.closingCash);
    }
  }, []);

  // Show the actual opening date, not the current system date
  const openingDate = tillSession?.openingTime ? new Date(tillSession.openingTime.replace(/-/g, "/")).toLocaleDateString() : new Date().toLocaleDateString();
  const [displayClock, setDisplayClock] = useState("");
  useEffect(() => {
    setDisplayClock(clock());
    const timerId = setInterval(() => setDisplayClock(clock()), 1000);
    return () => {
      clearInterval(timerId);
    };
  }, []);

  const [totalAmount, setTotalAmount] = useState(0);

  const onFieldInput = (e) => {
    const { name, value } = e.target;
    const denominationName = parseFloat(name);
    const denominationValue = parseInt(value);
    const denominationAmount = denominationName * denominationValue;
    const di = denominations.findIndex((d) => d.value === denominationName);
    if (di !== -1 && denominations[di]) {
      denominations[di].count = isNaN(denominationValue) ? "" : denominationValue;
      denominations[di].amount = isNaN(denominationAmount) ? 0 : denominationAmount;
    }
    let total = 0;
    denominations.forEach((denom) => {
      total += denom.amount || 0;
    });
    setTotalAmount(total);
    setDenominations([...denominations]);
  };

  const onFieldOut = (e) => {
    if (e.target.value === "") {
      const di = denominations.findIndex((d) => d.value === parseFloat(e.target.name));
      if (di === -1) return;
      denominations[di].count = 0;
      setDenominations([...denominations]);
    }
  };

  const onFieldButton = (field, action) => {
    if (action === "plus") denominations[field].count += 1;
    if (action === "minus" && denominations[field].count > 0) denominations[field].count -= 1;
    const denominationAmount = denominations[field].value * denominations[field].count;
    const denominationAmountValue = isNaN(denominationAmount) ? 0 : denominationAmount;
    denominations[field].amount = denominationAmountValue;
    let total = 0;
    denominations.forEach((denom) => {
      total += denom.amount;
    });
    setTotalAmount(total);
    setDenominations([...denominations]);
  };

  const confirmBtnRef = useRef(null);
  const cancelBtnRef = useRef(null);

  const excuteCloseTill = async () => {
    let obj = { cashIn: 0, cashOut: 0, pettyCashIn: 0, pettyCashOut: 0 };

    const totalDenominations = denominations.reduce((sum, denom) => sum + (denom.amount || 0), 0);
    if (cashExpectedAmount > 0) {
      if (totalDenominations === 0) {
        message.error(getMultilingualText(`${messages.errors.till_management.denominations_required}`));
        return;
      }
    }
    setLoading(true);
    try {
      const tillEvent = await db.tillEvents.where("tillSessionId").equals(sessionId).toArray();

      if (tillEvent.length > 0 && tillEvent[0]?.cashInOutData) {
        setCashValues(tillEvent[0].cashInOutData);
        obj = tillEvent[0].cashInOutData;
      }

      if (confirmBtnRef.current) confirmBtnRef.current.disabled = true;
      if (cancelBtnRef.current) cancelBtnRef.current.disabled = true;

      const updatedTillSession = { ...tillSession };
      const updatedAllPaymentsData = [...allPaymentsData];
      const aPi = updatedAllPaymentsData.findIndex((apd) => apd.name.toLowerCase() === "cash");

      if (aPi === -1) {
        console.error("Cash payment data not found.");
        if (confirmBtnRef.current) confirmBtnRef.current.disabled = false;
        if (cancelBtnRef.current) cancelBtnRef.current.disabled = false;
        setLoading(false);
        return;
      }

      let updatedAmount = 0;
      if (tillData?.tillAccess?.csBunit?.incDayOpening === "N") {
        updatedAmount = parseFloat(updatedAllPaymentsData[aPi].expectedAmount);
      } else {
        updatedAmount = parseFloat(updatedAllPaymentsData[aPi].expectedAmount) + parseFloat(totalOpeningAmount || 0);
      }

      updatedAllPaymentsData[aPi].expectedAmount = updatedAmount;
      updatedAllPaymentsData[aPi].amount = totalAmount || 0;
      updatedAllPaymentsData[aPi].actualAmount = totalAmount || 0;
      updatedAllPaymentsData[aPi].cashSaleAmount = parseFloat(cashSaleAmount?.toFixed(tillData.tillAccess.csBunit.currencies[0]?.prcPrecision || 2));
      updatedAllPaymentsData[aPi].difference = parseFloat(
        (totalAmount - parseFloat(updatedAmount - updatedAllPaymentsData[aPi].paymentReturn)).toFixed(tillData.tillAccess.csBunit.currencies[0]?.prcPrecision || 2)
      );
      updatedTillSession.cashInOutData = tillEvent[0]?.cashInOutData ? tillEvent[0].cashInOutData : obj;
      // BUG-2019: closingTime intentionally not stamped here (see fetchData effect above) —
      // this only advances to the next close-till screen, it does not confirm the close.
      updatedTillSession.closingCash = totalAmount || 0;
      updatedTillSession.closingCashDenominations = denominations || [];
      updatedTillSession.allPaymentsData = updatedAllPaymentsData;
      updatedTillSession.totalOrdersCount = totalOrdersCount || 0;

      await db.tillEvents.where("tillSessionId").equals(updatedTillSession.tillSessionId).modify(updatedTillSession);

      useSessionStore.getState().setTillSession(updatedTillSession);

      if (posConfig?.showCloseTillPaymentSummary === "Y") {
        history.push("/close-till-all");
      } else {
        history.push("/cash-to-keep");
      }
    } catch (error) {
      console.error("Error executing close till operation:", error);
    } finally {
      setLoading(false);
      if (confirmBtnRef.current) confirmBtnRef.current.disabled = false;
      if (cancelBtnRef.current) cancelBtnRef.current.disabled = false;
    }
  };

  let reconstructedObject;

  const rawtillAccessMeta = tillData?.tillAccess?.tillAccessMeta;
  if (rawtillAccessMeta) {
    const rawDataArray = tillData?.tillAccess?.tillAccessMeta;

    const closeTillObject = rawDataArray.find((item) => item.key === "Close Till Denominations");

    const closeTillValue = closeTillObject?.value;

    if (closeTillValue !== undefined) {
      const cleanedData = closeTillValue.replace(/[{}"]/g, "");
      const keyValuePairs = cleanedData.split(",");

      reconstructedObject = {};
      keyValuePairs.forEach((pair) => {
        if (pair.includes(":")) {
          const [key, value] = pair.split(":");

          // Check if both key and value are not undefined before trimming
          if (key !== undefined && value !== undefined) {
            reconstructedObject[key.trim()] = value.trim();
          }
        }
      });
    } else {
      // Handle the case where closeTillValue is undefined
      console.error("closeTillValue is undefined");
    }
  }

  if (!tillData?.tillAccess?.csBunit || !tillSession) {
    return null;
  }

  return (
    <Spin indicator={<LoadingOutlined style={{ fontSize: 40, marginTop: "18vh", color: "#2F3856" }} spin />} spinning={loading}>
      {loading ? (
        <div className="till-screen"></div>
      ) : (
        <div className="till-screen">
          <header className="till-header">
            <img src={MenuIcon} className="till-header__logo" alt="" />
            <span className="till-header__title">Close Till</span>
          </header>
          <div className="till-stepper">
            <div className="till-stepper__step till-stepper__step--active">
              <span className="till-stepper__circle">1</span>
              <span className="till-stepper__label">{getMultilingualText("Count Cash")}</span>
            </div>
            <div className="till-stepper__connector"></div>
            <div className="till-stepper__step">
              <span className="till-stepper__circle">2</span>
              <span className="till-stepper__label">{getMultilingualText("Reconcile")}</span>
            </div>
            <div className="till-stepper__connector"></div>
            <div className="till-stepper__step">
              <span className="till-stepper__circle">3</span>
              <span className="till-stepper__label">{getMultilingualText("Close")}</span>
            </div>
          </div>
          <div className="till-panel">
            <div className="till-panel__main">
              <div className="till-card">
                <div className="till-card__header">
                  <div className="till-denom-table__cell--denom till-card__header-col">
                    <span>{getMultilingualText("Denomination")}</span>
                  </div>
                  <div className="till-denom-table__cell--count till-card__header-col">
                    <span>{getMultilingualText("Count")}</span>
                  </div>
                  <div className="till-denom-table__cell--total till-card__header-col">
                    <span>{getMultilingualText("Total Amount")}</span>
                  </div>
                </div>
                <div className="till-card__body">
                  <Scrollbars className="closeTillScroll">
                    {denominations.map((denom, index) => (
                      <div className="till-denom-table__row" key={denom.value}>
                        <div className="till-denom-table__cell till-denom-table__cell--denom">
                          <Input tabIndex={-1} readOnly={true} className="till-denom-table__value" value={denom.value} />
                        </div>
                        <div className="till-denom-table__cell till-denom-table__cell--count">
                          <div className="till-denom-counter">
                            <button className="till-denom-counter__btn" type="button" onClick={() => onFieldButton(index, "minus")}>
                              <MinusOutlined />
                            </button>
                            <Form.Item className="till-denom-counter__input-wrap" style={{ marginBottom: 0 }}>
                              <Input name={`${denom.value}`} value={denom.count} className="till-denom-counter__input" onChange={onFieldInput} onBlur={onFieldOut} />
                            </Form.Item>
                            <button className="till-denom-counter__btn" type="button" onClick={() => onFieldButton(index, "plus")}>
                              <PlusOutlined />
                            </button>
                          </div>
                        </div>
                        <div className="till-denom-table__cell till-denom-table__cell--total">
                          <Input
                            tabIndex={-1}
                            className="till-denom-table__total"
                            readOnly={true}
                            value={`${currenciesList[0].symbolRightSide === "N" ? currenciesList[0].currSymbol : ""} ${denom.amount} ${
                              currenciesList[0].symbolRightSide === "Y" ? currenciesList[0].currSymbol : ""
                            }`}
                          />
                        </div>
                      </div>
                    ))}
                    <span tabIndex={0}></span>
                  </Scrollbars>
                </div>
              </div>
            </div>
            <div className="till-panel__sidebar">
              <div className="till-card--sidebar">
                <p className="till-summary__opened-on">{getMultilingualText("Opened On")}</p>
                <div className="till-summary__datetime">
                  <span>{openingDate}</span>
                  <span>{displayClock}</span>
                </div>
                <Form>
                  <p className="till-summary__section-title--lg">{getMultilingualText("Transactions")}</p>
                  <Scrollbars className="closeTillScroll2">
                    <Form.Item
                      style={{
                        marginBottom: "0.8vh",
                        display: reconstructedObject ? (reconstructedObject.OpeningAmount === "Y" ? "block" : "none") : "block",
                      }}
                    >
                      <p className="till-summary__field-label">{getMultilingualText("Opening Amount")}</p>
                      <Input
                        value={`${currenciesList[0].symbolRightSide === "N" ? currenciesList[0].currSymbol : ""} ${parseFloat(totalOpeningAmount).toFixed(
                          currencyType.stdPrecision
                        )} ${currenciesList[0].symbolRightSide === "Y" ? currenciesList[0].currSymbol : ""}`}
                        readOnly={true}
                        className="transactionAmtInput"
                        style={{ height: "5.5vh" }}
                      />
                    </Form.Item>
                    <Form.Item
                      style={{
                        marginBottom: "0.8vh",
                        display: reconstructedObject ? (reconstructedObject.SalesAmount === "Y" ? "block" : "none") : "block",
                      }}
                    >
                      <p className="till-summary__field-label">{getMultilingualText("Cash Sale Amount")}</p>
                      <Input
                        value={`${currenciesList[0].symbolRightSide === "N" ? currenciesList[0].currSymbol : ""} ${parseFloat(cashSaleAmount).toFixed(
                          currencyType.stdPrecision
                        )} ${currenciesList[0].symbolRightSide === "Y" ? currenciesList[0].currSymbol : ""}`}
                        readOnly={true}
                        className="transactionAmtInput"
                        style={{ height: "5.5vh", marginTop: "1vh" }}
                      />
                    </Form.Item>
                    <Form.Item
                      style={{
                        marginBottom: "0.8vh",
                        display: reconstructedObject ? (reconstructedObject.SalesReturnAmount === "Y" ? "block" : "none") : "block",
                      }}
                    >
                      <p className="till-summary__field-label">{getMultilingualText("Cash Sale Return Amount")}</p>
                      <Input
                        value={`${currenciesList[0].symbolRightSide === "N" ? currenciesList[0].currSymbol : ""} ${parseFloat(Math.abs(cashReturnAmount)).toFixed(
                          currencyType.stdPrecision
                        )} ${currenciesList[0].symbolRightSide === "Y" ? currenciesList[0].currSymbol : ""}`}
                        readOnly={true}
                        className="transactionAmtInput"
                        style={{ height: "5.5vh", marginTop: "1vh" }}
                      />
                    </Form.Item>
                    <Form.Item
                      style={{
                        marginBottom: "0.8vh",
                        display: reconstructedObject ? (reconstructedObject.CashIn === "Y" ? "block" : "none") : "block",
                      }}
                    >
                      <p className="till-summary__field-label">{getMultilingualText("Cash In")}</p>
                      <Input
                        value={`${currenciesList[0].symbolRightSide === "N" ? currenciesList[0].currSymbol : ""} ${parseFloat(cashValues.cashIn).toFixed(
                          currencyType.stdPrecision
                        )} ${currenciesList[0].symbolRightSide === "Y" ? currenciesList[0].currSymbol : ""}`}
                        readOnly={true}
                        className="transactionAmtInput"
                        style={{ height: "5.5vh" }}
                      />
                    </Form.Item>
                    <Form.Item
                      style={{
                        marginBottom: "0.8vh",
                        display: reconstructedObject ? (reconstructedObject.CashOut === "Y" ? "block" : "none") : "block",
                      }}
                    >
                      <p className="till-summary__field-label">{getMultilingualText("Cash Out")}</p>
                      <Input
                        value={`${currenciesList[0].symbolRightSide === "N" ? currenciesList[0].currSymbol : ""} ${parseFloat(cashValues.cashOut).toFixed(
                          currencyType.stdPrecision
                        )} ${currenciesList[0].symbolRightSide === "Y" ? currenciesList[0].currSymbol : ""}`}
                        readOnly={true}
                        className="transactionAmtInput"
                        style={{ height: "5.5vh" }}
                      />
                    </Form.Item>
                    <Form.Item
                      style={{
                        marginBottom: "0.8vh",
                        display: reconstructedObject ? (reconstructedObject.PettyCashIn === "Y" ? "block" : "none") : "block",
                      }}
                    >
                      <p className="till-summary__field-label">{getMultilingualText("Petty Cash In")}</p>
                      <Input
                        value={`${currenciesList[0].symbolRightSide === "N" ? currenciesList[0].currSymbol : ""} ${parseFloat(cashValues.pettyCashIn).toFixed(
                          currencyType.stdPrecision
                        )} ${currenciesList[0].symbolRightSide === "Y" ? currenciesList[0].currSymbol : ""}`}
                        readOnly={true}
                        className="transactionAmtInput"
                        style={{ height: "5.5vh" }}
                      />
                    </Form.Item>
                    <Form.Item
                      style={{
                        marginBottom: "0.8vh",
                        display: reconstructedObject ? (reconstructedObject.PettyCashOut === "Y" ? "block" : "none") : "block",
                      }}
                    >
                      <p className="till-summary__field-label">{getMultilingualText("Petty Cash Out")}</p>
                      <Input
                        value={`${currenciesList[0].symbolRightSide === "N" ? currenciesList[0].currSymbol : ""} ${parseFloat(cashValues.pettyCashOut).toFixed(
                          currencyType.stdPrecision
                        )} ${currenciesList[0].symbolRightSide === "Y" ? currenciesList[0].currSymbol : ""}`}
                        readOnly={true}
                        className="transactionAmtInput"
                        style={{ height: "5.5vh" }}
                      />
                    </Form.Item>
                    <Form.Item
                      style={{
                        marginBottom: "0.8vh",
                        display: reconstructedObject ? (reconstructedObject.ExpectedCash === "Y" ? "block" : "none") : "block",
                      }}
                    >
                      <p className="till-summary__field-label">{getMultilingualText("Expected Cash")}</p>
                      <Input
                        value={`${currenciesList[0].symbolRightSide === "N" ? currenciesList[0].currSymbol : ""} ${parseFloat(cashExpectedAmount).toFixed(
                          currencyType.stdPrecision
                        )} ${currenciesList[0].symbolRightSide === "Y" ? currenciesList[0].currSymbol : ""}`}
                        readOnly={true}
                        className="transactionAmtInput"
                        style={{ height: "5.5vh", marginTop: "1vh" }}
                      />
                    </Form.Item>
                    <Form.Item style={{ marginBottom: "0.8vh" }}>
                      <p className="till-summary__field-label">{getMultilingualText("Actual Cash")}</p>
                      <Input
                        value={`${currenciesList[0].symbolRightSide === "N" ? currenciesList[0].currSymbol : ""} ${parseFloat(totalAmount).toFixed(currencyType.stdPrecision)} ${
                          currenciesList[0].symbolRightSide === "Y" ? currenciesList[0].currSymbol : ""
                        }`}
                        readOnly={true}
                        className="transactionAmtInput"
                        style={{ height: "5.5vh", marginTop: "1vh" }}
                      />
                    </Form.Item>
                  </Scrollbars>
                </Form>
                <div className="till-actions--sticky">
                  <div className="till-actions">
                    <Button ref={cancelBtnRef} className="till-btn--secondary">
                      {hideBackButton ? <Link to={"/till"}>{getMultilingualText("Back")}</Link> : <Link to={"/pos"}>{getMultilingualText("Back")}</Link>}
                    </Button>
                    <Button ref={confirmBtnRef} className="till-btn--primary" onClick={excuteCloseTill} loading={loading} disabled={loading}>
                      {loading ? getMultilingualText("Processing...") : getMultilingualText("Next")}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Spin>
  );
};

export default CloseTill;
